import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, FileText, UploadCloud, Clock, CheckCircle2, AlertTriangle, Trash2, RotateCcw, Lock } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Button } from './Button';
import { RevisionUploadWizard } from './RevisionUploadWizard';
import { Spinner } from './Spinner';
import { toast } from '@/state/toast';
import { useAuth } from '@/state/auth';
import type { SnapshotsResponse, Snapshot, DocumentsResponse, DocumentVersion, IngestStatus } from '@/types/api';

// RBAC: проектировщик (gip) и эксперт не загружают документы (бэкенд /ingest → 403).
// gip прикладывает исправление к ответу на замечание.
const UPLOAD_BLOCKED_ROLES = ['gip', 'expert'];

interface Props {
  projectId: string;
  /** 11.5: заказчику скрываем деструктив (удаление тома) и техничные Snapshot ID. */
  isCustomer?: boolean;
}

// 15.B Сигнал конфиденциальности. Тихая строка доверия у зоны загрузки.
// Утверждено владельцем 2026-07-06 (Вариант A — минимальный, только проверяемые факты).
const CONFIDENTIALITY_NOTE = 'Ваши документы хранятся в защищённом хранилище. Доступ — только у участников вашего проекта.';

export function DocumentsTab({ projectId, isCustomer = false }: Props) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const role = useAuth((s) => s.user?.default_role);
  const canUpload = !UPLOAD_BLOCKED_ROLES.includes((role || '').toLowerCase());

  const { data, isLoading } = useQuery({
    queryKey: ['snapshots', projectId],
    queryFn: () => api.get<SnapshotsResponse>(`/projects/${projectId}/snapshots`),
  });
  const allSnapshots = data?.snapshots || [];
  // Скрываем архивные (invalidated_at != null) по умолчанию — иначе user
  // видит «две редакции» после каждой повторной загрузки, хотя реально работает
  // только последняя. Backend честно инвалидирует старый snapshot при изменении
  // состава документов, но пользователю это видеть не нужно.
  const archivedCount = allSnapshots.filter((s) => s.invalidated_at != null).length;
  const snapshots = showArchived ? allSnapshots : allSnapshots.filter((s) => s.invalidated_at == null);

  // Polling /documents — стартует сразу, выключается когда все версии в ready.
  // Если есть хотя бы одна failed/analyzing/uploaded версия — опрос каждые 10 сек.
  const { data: docsData } = useQuery({
    queryKey: ['documents', projectId],
    queryFn: () => api.get<DocumentsResponse>(`/projects/${projectId}/documents`),
    refetchInterval: (query) => {
      const docs = (query.state.data as DocumentsResponse | undefined)?.documents || [];
      if (docs.length === 0) return 10000;  // ещё нет данных, проверяем
      const inFlight = docs.some((d) => d.ingest_status === 'uploaded' || d.ingest_status === 'analyzing');
      return inFlight ? 10000 : false;
    },
  });
  const documents = docsData?.documents || [];

  // Тост об ошибке анализа — показываем единожды на каждую failed-версию.
  const failedNotifiedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    documents
      .filter((d) => d.ingest_status === 'failed')
      .forEach((d) => {
        if (failedNotifiedRef.current.has(d.version_id)) return;
        failedNotifiedRef.current.add(d.version_id);
        toast.error(`Не удалось проанализировать «${d.title}»${d.ingest_error ? `: ${d.ingest_error.slice(0, 80)}` : ''}`);
      });
  }, [documents]);

  // Удаление документа из проекта (admin или curator-владелец).
  const deleteDoc = useMutation({
    mutationFn: (documentId: string) =>
      api.delete<{ ok: boolean }>(`/projects/${projectId}/documents/${documentId}`),
    onSuccess: () => {
      toast.success('Документ удалён');
      qc.invalidateQueries({ queryKey: ['documents', projectId] });
      qc.invalidateQueries({ queryKey: ['snapshots', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось удалить документ'),
  });

  // Перезапуск анализа для failed-документов. Сбрасывает один (последний)
  // document_version в 'uploaded' и кикает BackgroundTask заново.
  const retryDoc = useMutation({
    mutationFn: (documentId: string) =>
      api.post<{ ok: boolean; scheduled: boolean }>(`/projects/${projectId}/documents/${documentId}/retry`, {}),
    onSuccess: (r) => {
      if (r.scheduled) {
        toast.success('Анализ перезапущен');
        // Сбросим failed-уведомление чтобы при следующей ошибке снова показался тост.
        failedNotifiedRef.current.clear();
      } else {
        toast.info('Документ не требует повторного анализа');
      }
      qc.invalidateQueries({ queryKey: ['documents', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось перезапустить анализ'),
  });

  // 9.3: новая ВЕРСИЯ ФАЙЛА существующего документа (досылка/исправленный том).
  // Не создаёт новую редакцию проекта — только version_seq+1 у документа.
  const uploadVersion = useMutation({
    mutationFn: async ({ documentId, file }: { documentId: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', projectId);
      fd.append('document_id', documentId);
      const res = await fetch('/ingest', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body && typeof body.detail === 'string') detail = body.detail;
        } catch {/* ignore */}
        throw new ApiError(res.status, detail);
      }
      return res.json();
    },
    onSuccess: (r: { deduplicated?: boolean }) => {
      toast.success(r?.deduplicated
        ? 'Файл идентичен текущей версии — новая версия не создана'
        : 'Новая версия файла загружена, анализ запущен');
      qc.invalidateQueries({ queryKey: ['documents', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось загрузить версию'),
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      // Шаг 1: ingest каждого файла (создаются записи document + document_version).
      const results: { name: string; ok: boolean; error?: string }[] = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('project_id', projectId);
        try {
          const res = await fetch('/ingest', { method: 'POST', credentials: 'include', body: fd });
          if (!res.ok) {
            let detail = `HTTP ${res.status}`;
            try {
              const body = await res.json();
              if (body && typeof body.detail === 'string') detail = body.detail;
            } catch {/* ignore */}
            throw new ApiError(res.status, detail);
          }
          results.push({ name: f.name, ok: true });
        } catch (e) {
          results.push({ name: f.name, ok: false, error: e instanceof Error ? e.message : 'error' });
        }
      }

      // Шаг 2: если хотя бы один файл успешно проиндексирован — создаём snapshot.
      // Snapshot = «редакция» в терминах UI. Без него файлы есть, но в списке
      // «Редакций» пусто, что выглядит как «загрузка не сработала».
      // POST /projects/{id}/snapshots безопасен: если содержимое не изменилось,
      // backend вернёт 400 «no changes» — это не ошибка для UX.
      const ingestOk = results.some((r) => r.ok);
      let snapshotCreated = false;
      let snapshotError: string | null = null;
      if (ingestOk) {
        try {
          const r = await fetch(`/projects/${projectId}/snapshots`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
          });
          if (r.ok) {
            snapshotCreated = true;
          } else {
            let detail = `HTTP ${r.status}`;
            try {
              const b = await r.json();
              if (b && typeof b.detail === 'string') detail = b.detail;
            } catch {/* ignore */}
            // 400 "no changes" — не критично, просто файл уже в текущем snapshot.
            if (r.status === 400 && /no changes|no document/i.test(detail)) {
              snapshotCreated = false;
              snapshotError = null;
            } else {
              snapshotError = detail;
            }
          }
        } catch (e) {
          snapshotError = e instanceof Error ? e.message : 'snapshot failed';
        }
      }

      return { files: results, snapshotCreated, snapshotError };
    },
    onSuccess: (r) => {
      const okCount = r.files.filter((x) => x.ok).length;
      const failed = r.files.filter((x) => !x.ok);

      if (failed.length === 0 && r.snapshotCreated) {
        toast.success(`Загружено ${okCount} ${plural(okCount, 'файл', 'файла', 'файлов')} · анализируется в фоне`);
      } else if (failed.length === 0 && r.snapshotError) {
        toast.error(`Файлы загружены (${okCount}), но не удалось создать редакцию: ${r.snapshotError}`);
      } else if (failed.length === 0) {
        // Файлы залились, snapshot не нужен (контент не изменился).
        toast.success(`Загружено ${okCount} ${plural(okCount, 'файл', 'файла', 'файлов')} · анализируется в фоне`);
      } else {
        toast.error(`${failed[0].name}: ${failed[0].error || 'ошибка'}${failed.length > 1 ? ` (+ ещё ${failed.length - 1})` : ''}`);
      }
      qc.invalidateQueries({ queryKey: ['snapshots', projectId] });
      qc.invalidateQueries({ queryKey: ['documents', projectId] });
      qc.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Ошибка загрузки'),
    onSettled: () => setUploading(false),
  });

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // Фильтр по расширениям (двойная защита — accept attr может пропустить через DnD).
    const allowed = ['.pdf', '.dwg', '.dxf', '.docx'];
    const accepted: File[] = [];
    const rejected: string[] = [];
    Array.from(files).forEach((f) => {
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      if (allowed.includes(ext)) accepted.push(f);
      else rejected.push(f.name);
    });
    if (rejected.length > 0) {
      toast.error(`Не поддерживаемый формат: ${rejected.join(', ')}. Только PDF/DWG/DXF/DOCX.`);
    }
    if (accepted.length === 0) return;
    setUploading(true);
    upload.mutate(accepted);
  };

  // Drag-and-drop state — подсвечиваем drop-zone, когда над ней файл.
  const [dragOver, setDragOver] = useState(false);
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    handleFiles(e.dataTransfer.files);
  };

  if (isLoading) {
    return <div style={{ padding: '64px 0', textAlign: 'center' }}><Spinner size={24} className="text-muted" /></div>;
  }

  return (
    <div>
      <header className="flex items-end justify-between" style={{ marginBottom: 24, gap: 16 }}>
        <div>
          <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>Редакции документации</h3>
          <p className="text-muted mt-1" style={{ fontSize: 13 }}>
            {snapshots.length} {plural(snapshots.length, 'редакция', 'редакции', 'редакций')}
            {snapshots[0] && ` · последняя ${new Date(snapshots[0].created_at).toLocaleDateString('ru-RU')}`}
            {archivedCount > 0 && (
              <>
                {' · '}
                <label className="text-muted" style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    style={{ marginRight: 6, verticalAlign: 'middle' }}
                  />
                  Показать архивные ({archivedCount})
                </label>
              </>
            )}
          </p>
        </div>
        {canUpload && (
          <Button
            variant="primary"
            icon={<Upload size={18} />}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >{uploading ? 'Загрузка…' : 'Загрузить новую редакцию'}</Button>
        )}
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".pdf,.dwg,.dxf,.docx,.xlsx,.xls,.pptx,.html,.htm"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
          style={{ display: 'none' }}
        />
      </header>

      {canUpload && (
      <button
        type="button"
        onClick={() => { if (!uploading) fileRef.current?.click(); }}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        disabled={uploading}
        className="w-full rounded-card text-center"
        style={{
          padding: 32,
          background: dragOver ? 'rgba(245,158,11,.08)' : '#F3F4F6',
          border: dragOver ? '2px dashed #F59E0B' : '2px dashed #E5E7EB',
          cursor: uploading ? 'wait' : 'pointer',
          marginBottom: 32,
          transition: 'background 120ms, border-color 120ms',
        }}
        aria-label="Загрузить редакцию документации"
      >
        <UploadCloud size={40} stroke={dragOver ? '#F59E0B' : '#6B7280'} style={{ margin: '0 auto' }} />
        <div className="text-ink mt-3" style={{ fontSize: 15, fontWeight: 500 }}>
          {uploading ? 'Загрузка файлов…' : dragOver ? 'Отпустите файл, чтобы загрузить' : 'Перетащите PDF/DWG/DXF/DOCX/XLSX/PPTX/HTML или нажмите для выбора'}
        </div>
        <div className="text-muted mt-1" style={{ fontSize: 11 }}>До 200 МБ · pdf, dwg, dxf, docx, xlsx, pptx, html</div>
      </button>
      )}

      {/* 15.B Сигнал конфиденциальности — тихая строка доверия (не баннер). */}
      <div
        className="text-muted flex items-center"
        style={{ gap: 6, marginTop: -20, marginBottom: 28, fontSize: 11, lineHeight: 1.4 }}
      >
        <Lock size={12} style={{ flexShrink: 0 }} />
        <span>{CONFIDENTIALITY_NOTE}</span>
      </div>

      {canUpload && documents.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <RevisionUploadWizard
            projectId={projectId}
            documents={Array.from(
              new Map(documents.map((d) => [d.document_id, { document_id: d.document_id, title: d.title || '(без названия)' }])).values()
            )}
          />
        </div>
      )}

      <div className="flex flex-col" style={{ gap: 0 }}>
        {snapshots.length === 0 && (
          <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>
            Документов ещё нет. Загрузите первую редакцию.
          </p>
        )}
        {snapshots.map((s, idx) => (
          <SnapshotRow
            key={s.id}
            snapshot={s}
            version={snapshots.length - idx}
            isLast={idx === snapshots.length - 1}
            active={idx === 0}
            documents={documents}
            totalAnalyzing={documents.filter((d) => d.ingest_status === 'analyzing').length}
            onDeleteDocument={(documentId) => deleteDoc.mutate(documentId)}
            deletingId={deleteDoc.isPending ? (deleteDoc.variables as string | undefined) : undefined}
            onRetryDocument={(documentId) => retryDoc.mutate(documentId)}
            retryingId={retryDoc.isPending ? (retryDoc.variables as string | undefined) : undefined}
            onUploadVersion={(documentId, file) => uploadVersion.mutate({ documentId, file })}
            isCustomer={isCustomer}
          />
        ))}
      </div>
    </div>
  );
}

function SnapshotRow({
  snapshot, version, isLast, active, documents, totalAnalyzing,
  onDeleteDocument, deletingId, onRetryDocument, retryingId, onUploadVersion, isCustomer,
}: {
  snapshot: Snapshot;
  version: number;
  isLast: boolean;
  active: boolean;
  documents: DocumentVersion[];
  totalAnalyzing: number;
  onDeleteDocument: (documentId: string) => void;
  deletingId?: string;
  onRetryDocument: (documentId: string) => void;
  onUploadVersion: (documentId: string, file: File) => void;
  retryingId?: string;
  isCustomer?: boolean;
}) {
  const date = new Date(snapshot.created_at);
  const isArchived = snapshot.invalidated_at != null;
  const circleColor = active ? '#F59E0B' : isArchived ? '#9CA3AF' : '#10B981';

  return (
    <div className="flex" style={{ gap: 16, paddingBottom: isLast ? 0 : 32 }}>
      <div style={{ width: 56, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span
          className="rounded-pill flex items-center justify-center font-heading"
          style={{ width: 40, height: 40, background: circleColor, color: '#FFFFFF', fontWeight: 600, fontSize: 16 }}
        >{version}</span>
        {!isLast && <span style={{ flex: 1, width: 1, background: '#E5E7EB', marginTop: 4 }} />}
      </div>
      <div className="flex-1 bg-white border border-border rounded-card" style={{ padding: 24 }}>
        <div className="flex items-center justify-between">
          <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>
            Редакция №{version} {active ? '(текущая)' : ''}
          </h4>
          <span className="text-muted tabular" style={{ fontSize: 12 }}>
            {date.toLocaleDateString('ru-RU')}, {date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
            {isArchived && <span className="rounded-pill bg-surface text-muted ml-2" style={{ padding: '2px 8px', fontSize: 11 }}>архивная</span>}
          </span>
        </div>

        <div className="mt-4">
          <div className="grid text-muted uppercase tracking-wider" style={{ gridTemplateColumns: '1fr 90px 180px 60px', fontSize: 11, fontWeight: 600, padding: '0 0 8px', borderBottom: '1px solid #E5E7EB', gap: 12 }}>
            <div>Документ</div>
            <div>Формат</div>
            <div>Статус анализа</div>
            <div className="text-right" aria-label="Действия">⋯</div>
          </div>

          {documents.length === 0 && (
            <p className="text-muted" style={{ padding: '24px 0', fontSize: 13, textAlign: 'center' }}>
              В этой редакции пока нет документов.
            </p>
          )}

          {groupDocuments(documents).map((g) =>
            g.kind === 'single' ? (
              <DocumentRow
                key={g.doc.version_id}
                doc={g.doc}
                totalAnalyzing={totalAnalyzing}
                onDelete={() => onDeleteDocument(g.doc.document_id)}
                isDeleting={deletingId === g.doc.document_id}
                onRetry={() => onRetryDocument(g.doc.document_id)}
                isRetrying={retryingId === g.doc.document_id}
                onUploadVersion={(file) => onUploadVersion(g.doc.document_id, file)}
              isCustomer={isCustomer}
              />
            ) : (
              <GroupRow
                key={g.key}
                group={g}
                totalAnalyzing={totalAnalyzing}
                onDeleteDocuments={(ids) => ids.forEach(onDeleteDocument)}
                onRetryDocuments={(ids) => ids.forEach(onRetryDocument)}
                retryingIds={new Set(retryingId ? [retryingId] : [])}
                isCustomer={isCustomer}
              />
            )
          )}
        </div>

        {/* 11.5: Snapshot ID/хеш — техничное, заказчику не показываем. */}
        {!isCustomer && (
        <p className="text-muted mt-3" style={{ fontSize: 11 }}>
          Snapshot ID: <span className="tabular">{snapshot.id.slice(0, 8)}</span>
          {snapshot.content_hash && <> · хеш {snapshot.content_hash.slice(0, 8)}</>}
        </p>
        )}
      </div>
    </div>
  );
}

function DocumentRow({
  doc, onDelete, isDeleting, totalAnalyzing, onRetry, isRetrying, onUploadVersion, isCustomer,
}: {
  doc: DocumentVersion;
  onDelete: () => void;
  isDeleting: boolean;
  totalAnalyzing: number;
  onRetry: () => void;
  isRetrying: boolean;
  onUploadVersion?: (file: File) => void;
  isCustomer?: boolean;
}) {
  const versionInputRef = useRef<HTMLInputElement>(null);
  // Live-таймер для статуса analyzing: тикает каждую секунду в браузере, чтобы
  // счётчик не «прыгал» только при refetch /documents (раз в 10 сек).
  const isAnalyzing = doc.ingest_status === 'analyzing' && doc.ingest_started_at != null;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isAnalyzing) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [isAnalyzing]);
  const elapsed = isAnalyzing && doc.ingest_started_at
    ? Math.max(0, Math.round((now - new Date(doc.ingest_started_at).getTime()) / 1000))
    : null;

  // Оценка общего времени анализа документа (эвристика, см. estimateTotalSec).
  // Если анализируется >1 документ параллельно — масштабируем по факту, что
  // OpenAI rate limits и I/O делают параллельные задачи медленнее.
  const estimatedTotal = isAnalyzing
    ? estimateTotalSec(doc.format, doc.page_count, totalAnalyzing)
    : null;
  const eta =
    isAnalyzing && elapsed !== null && estimatedTotal !== null
      ? Math.max(0, estimatedTotal - elapsed)
      : null;
  // Прогресс-бар: 0..95% пока не завершено. Если elapsed превысил оценку —
  // фиксируем на 95%, не пугаем пользователя «100%» на бесконечно зависшей строке.
  const progressPct =
    isAnalyzing && elapsed !== null && estimatedTotal !== null
      ? Math.min(95, Math.round((elapsed / estimatedTotal) * 100))
      : null;

  const handleDelete = () => {
    const ok = window.confirm(`Удалить документ «${doc.title || '(без названия)'}»? Это действие нельзя отменить.`);
    if (ok) onDelete();
  };

  return (
    <div
      className="grid items-center"
      style={{
        gridTemplateColumns: '1fr 90px 180px 60px',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid #E5E7EB',
        fontSize: 13,
        opacity: isDeleting || isRetrying ? 0.5 : 1,
        transition: 'opacity 120ms',
      }}
    >
      <div className="flex items-center" style={{ gap: 8, minWidth: 0 }}>
        <FileText size={14} stroke="#6B7280" style={{ flexShrink: 0 }} />
        <span
          className="text-ink"
          style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={doc.title || '(без названия)'}
        >
          {doc.title || '(без названия)'}
        </span>
        {doc.page_count ? (
          <span className="text-muted" style={{ fontSize: 11, flexShrink: 0 }}>· {doc.page_count} стр.</span>
        ) : null}
      </div>
      <div className="text-muted tabular" style={{ fontSize: 12 }}>
        {doc.format || '—'} · версия файла {doc.version_seq}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div className="flex items-center" style={{ gap: 6 }}>
          <IngestStatusIcon status={doc.ingest_status} />
          <IngestStatusText
            status={doc.ingest_status}
            eta={eta}
            error={doc.ingest_error}
          />
        </div>
        {isAnalyzing && progressPct !== null && (
          <div
            aria-label="Прогресс анализа"
            style={{
              height: 3,
              width: '100%',
              background: '#F3F4F6',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progressPct}%`,
                background: '#F59E0B',
                transition: 'width 1s linear',
              }}
            />
          </div>
        )}
      </div>
      <div className="flex items-center justify-end" style={{ gap: 4 }}>
        {onUploadVersion && (
          <>
            <input
              ref={versionInputRef}
              type="file"
              accept=".pdf,.dxf,.xlsx,.xls,.pptx,.html,.htm"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUploadVersion(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => versionInputRef.current?.click()}
              disabled={isDeleting || isRetrying}
              aria-label={`Загрузить новую версию файла ${doc.title || ''}`}
              title="Загрузить новую версию файла (досылка или исправленный том)"
              className="text-muted hover:text-ink"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 0 }}
            >
              <UploadCloud size={16} />
            </button>
          </>
        )}
        {doc.ingest_status === 'failed' && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying || isDeleting}
            aria-label={`Повторить анализ документа ${doc.title || ''}`}
            title="Повторить анализ"
            className="text-muted hover:text-ink"
            style={{ background: 'none', border: 'none', cursor: isRetrying ? 'wait' : 'pointer', padding: 4, lineHeight: 0 }}
          >
            <RotateCcw size={16} />
          </button>
        )}
        {/* 11.5: удаление тома — деструктив, заказчику (приёмка) не показываем. */}
        {!isCustomer && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={isDeleting || isRetrying}
          aria-label={`Удалить документ ${doc.title || ''}`}
          title="Удалить документ"
          className="text-muted hover:text-rose"
          style={{ background: 'none', border: 'none', cursor: isDeleting ? 'wait' : 'pointer', padding: 4, lineHeight: 0 }}
        >
          <Trash2 size={16} />
        </button>
        )}
      </div>
    </div>
  );
}

function IngestStatusIcon({ status }: { status: IngestStatus }) {
  if (status === 'uploaded') return <Clock size={16} stroke="#9CA3AF" />;
  if (status === 'analyzing') return <Spinner size={16} className="text-amber" />;
  if (status === 'ready') return <CheckCircle2 size={16} stroke="#10B981" />;
  return <AlertTriangle size={16} stroke="#EF4444" />;
}

function IngestStatusText({
  status,
  eta,
  error,
}: {
  status: IngestStatus;
  eta: number | null;
  error: string | null;
}) {
  if (status === 'uploaded') return <span style={{ color: '#6B7280' }}>Загружен. В очереди на анализ</span>;
  if (status === 'analyzing') {
    if (eta === null) {
      return <span style={{ color: '#B45309' }}>Анализируется…</span>;
    }
    if (eta <= 0) {
      // Эвристика занизила оценку — реальная обработка дольше предсказанной.
      // Не пугаем «100%», говорим что заканчивается.
      return <span style={{ color: '#B45309' }}>Анализируется · заканчивается</span>;
    }
    return <span style={{ color: '#B45309' }}>Анализируется · осталось {formatEta(eta)}</span>;
  }
  if (status === 'ready') return <span style={{ color: '#10B981' }}>Готов</span>;
  return (
    <span style={{ color: '#EF4444' }}>
      Ошибка{error ? `: ${error.slice(0, 80)}` : ''}
    </span>
  );
}

/** Человекочитаемое время «осталось ~N» — округляем до читаемых единиц. */
function formatEta(seconds: number): string {
  if (seconds < 60) {
    // Округление до 10 сек, иначе цифры скачут каждую секунду и нервируют.
    const rounded = Math.max(10, Math.ceil(seconds / 10) * 10);
    return `~${rounded} сек`;
  }
  const mins = Math.ceil(seconds / 60);
  return `~${mins} мин`;
}

/**
 * Эвристика времени анализа документа.
 *
 * Pipeline ~ pdfplumber (1-2 сек/стр) + embeddings (батч) + vision
 * (CONSOLIDA_MAX_VISION_PAGES страниц × ~10-12 сек/стр на gpt-4o) + Neo4j.
 * Параллельные документы делят OpenAI rate limit → масштабируем линейно
 * до 4 параллельных, дальше насыщение.
 *
 * Это **оценка** для UX-индикатора, а не SLA. Часто промахивается на ±30%.
 */
function estimateTotalSec(format: string, pageCount: number | null, totalAnalyzing: number): number {
  const fmt = (format || '').toUpperCase();
  const pages = pageCount && pageCount > 0 ? pageCount : 1;
  // Базовая оценка одиночного запуска.
  let base: number;
  if (fmt === 'DXF' || fmt === 'DWG') {
    base = 30 + Math.min(pages, 12) * 3;  // CAD парсится быстро
  } else if (fmt === 'DOCX') {
    base = 20;
  } else {
    // PDF: vision ~12 сек × min(pages, MAX_VISION_PAGES=20) + текст 1.5 сек/стр + 30 сек overhead
    const visionPages = Math.min(pages, 20);
    base = 30 + visionPages * 12 + pages * 1.5;
  }
  // Контеншн: параллельные документы конкурируют за OpenAI и I/O.
  const concurrency = Math.max(1, Math.min(totalAnalyzing, 4));
  return Math.round(base * (1 + (concurrency - 1) * 0.6));
}

/**
 * Группировка PDF+DXF/DWG как одного чертежа.
 *
 * Объединяем версии по title_stem только если форматы РАЗНЫЕ (PDF+DXF/DWG).
 * Если совпадают (PDF+PDF — две версии одного документа) — оставляем
 * раздельно (это уже отображается через version_seq).
 *
 * Порядок результата сохраняет исходный (по created_at, version_seq).
 */
type DocGroup =
  | { kind: 'single'; doc: DocumentVersion }
  | {
      kind: 'pair';
      key: string;
      stem: string;
      docs: DocumentVersion[];
      combinedFormats: string[];
    };

function groupDocuments(documents: DocumentVersion[]): DocGroup[] {
  // 1. Считаем форматы по stem.
  const formatsByStem = new Map<string, Set<string>>();
  for (const d of documents) {
    const stem = (d.title_stem || '').trim();
    if (!stem) continue;
    const fmt = (d.format || '').toUpperCase();
    if (!fmt) continue;
    if (!formatsByStem.has(stem)) formatsByStem.set(stem, new Set());
    formatsByStem.get(stem)!.add(fmt);
  }

  // 2. Идём по documents, складываем в группы по (stem) если в группе ≥2 формата.
  const groups: DocGroup[] = [];
  const seenStems = new Set<string>();
  for (const d of documents) {
    const stem = (d.title_stem || '').trim();
    const formats = stem ? formatsByStem.get(stem) : null;
    const isPair = !!formats && formats.size > 1;
    if (!isPair) {
      groups.push({ kind: 'single', doc: d });
      continue;
    }
    if (seenStems.has(stem)) continue;
    seenStems.add(stem);
    const members = documents.filter(
      (x) => (x.title_stem || '').trim() === stem
    );
    groups.push({
      kind: 'pair',
      key: `stem:${stem}`,
      stem,
      docs: members,
      combinedFormats: Array.from(formats!).sort(),
    });
  }
  return groups;
}

/** Худший статус по приоритету: failed > analyzing > uploaded > ready. */
function worstStatus(docs: DocumentVersion[]): IngestStatus {
  const order: Record<IngestStatus, number> = {
    failed: 0,
    uploaded: 1,
    analyzing: 2,
    ready: 3,
  };
  let worst: IngestStatus = 'ready';
  for (const d of docs) {
    if (order[d.ingest_status] < order[worst]) worst = d.ingest_status;
  }
  return worst;
}

function GroupRow({
  group,
  totalAnalyzing,
  onDeleteDocuments,
  onRetryDocuments,
  retryingIds,
  isCustomer,
}: {
  group: Extract<DocGroup, { kind: 'pair' }>;
  totalAnalyzing: number;
  onDeleteDocuments: (documentIds: string[]) => void;
  onRetryDocuments: (documentIds: string[]) => void;
  retryingIds: Set<string>;
  isCustomer?: boolean;
}) {
  const status = worstStatus(group.docs);
  // Title — берём из первого члена (они одинаковые по stem, но могут
  // отличаться регистром/расширением; для UI важен живой заголовок).
  const first = group.docs[0];
  const title = first.title || '(без названия)';
  const totalPages = group.docs.reduce(
    (sum, d) => sum + (d.page_count || 0),
    0,
  );

  // ETA/elapsed — берём максимум среди членов (показываем «худший случай»).
  // Используем те же эвристики, что для DocumentRow.
  const analyzingMembers = group.docs.filter(
    (d) => d.ingest_status === 'analyzing' && d.ingest_started_at != null,
  );
  const isAnalyzing = analyzingMembers.length > 0;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isAnalyzing) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [isAnalyzing]);
  const elapsed = isAnalyzing
    ? Math.max(
        0,
        ...analyzingMembers.map((d) =>
          Math.round((now - new Date(d.ingest_started_at!).getTime()) / 1000),
        ),
      )
    : null;
  const estimatedTotal = isAnalyzing
    ? Math.max(
        ...group.docs.map((d) =>
          estimateTotalSec(d.format, d.page_count, totalAnalyzing),
        ),
      )
    : null;
  const eta =
    isAnalyzing && elapsed !== null && estimatedTotal !== null
      ? Math.max(0, estimatedTotal - elapsed)
      : null;
  const progressPct =
    isAnalyzing && elapsed !== null && estimatedTotal !== null
      ? Math.min(95, Math.round((elapsed / estimatedTotal) * 100))
      : null;

  // Аккумулированный текст ошибки — первый failed член.
  const failedMember = group.docs.find((d) => d.ingest_status === 'failed');

  const handleDelete = () => {
    const formatLabel = group.combinedFormats.join(' + ');
    const ok = window.confirm(
      `Удалить пару ${formatLabel} «${title}»? Это удалит ${group.docs.length} ${plural(group.docs.length, 'документ', 'документа', 'документов')} (все форматы) и не может быть отменено.`,
    );
    if (!ok) return;
    onDeleteDocuments(group.docs.map((d) => d.document_id));
  };

  // Failed-документы в группе, которые ещё не повторяются.
  const failedDocs = group.docs.filter(
    (d) => d.ingest_status === 'failed' && !retryingIds.has(d.document_id),
  );
  const hasFailedToRetry = failedDocs.length > 0;
  const anyRetrying = group.docs.some((d) => retryingIds.has(d.document_id));

  const handleRetry = () => {
    onRetryDocuments(failedDocs.map((d) => d.document_id));
  };

  return (
    <div
      className="grid items-center"
      style={{
        gridTemplateColumns: '1fr 90px 180px 60px',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid #E5E7EB',
        fontSize: 13,
        opacity: anyRetrying ? 0.5 : 1,
        transition: 'opacity 120ms',
      }}
    >
      <div className="flex items-center" style={{ gap: 8, minWidth: 0 }}>
        <FileText size={14} stroke="#6B7280" style={{ flexShrink: 0 }} />
        <span
          className="text-ink"
          style={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={title}
        >
          {title}
        </span>
        {totalPages > 0 ? (
          <span
            className="text-muted"
            style={{ fontSize: 11, flexShrink: 0 }}
          >
            · {totalPages} стр.
          </span>
        ) : null}
      </div>
      <div className="tabular" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          className="rounded-pill"
          style={{
            padding: '2px 8px',
            fontSize: 11,
            fontWeight: 600,
            background: 'rgba(59,130,246,.12)',
            color: '#1D4ED8',
            border: '1px solid rgba(59,130,246,.25)',
            whiteSpace: 'nowrap',
          }}
          title="PDF и CAD-чертёж одного листа — анализ объединён"
        >
          {group.combinedFormats.join(' + ')}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          minWidth: 0,
        }}
      >
        <div className="flex items-center" style={{ gap: 6 }}>
          <IngestStatusIcon status={status} />
          <IngestStatusText
            status={status}
            eta={eta}
            error={failedMember?.ingest_error || null}
          />
        </div>
        {isAnalyzing && progressPct !== null && (
          <div
            aria-label="Прогресс анализа"
            style={{
              height: 3,
              width: '100%',
              background: '#F3F4F6',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progressPct}%`,
                background: '#F59E0B',
                transition: 'width 1s linear',
              }}
            />
          </div>
        )}
      </div>
      <div className="flex items-center justify-end" style={{ gap: 4 }}>
        {hasFailedToRetry && (
          <button
            type="button"
            onClick={handleRetry}
            disabled={anyRetrying}
            aria-label={`Повторить анализ пары ${group.combinedFormats.join('+')} ${title}`}
            title={`Повторить анализ (${failedDocs.length})`}
            className="text-muted hover:text-ink"
            style={{ background: 'none', border: 'none', cursor: anyRetrying ? 'wait' : 'pointer', padding: 4, lineHeight: 0 }}
          >
            <RotateCcw size={16} />
          </button>
        )}
        {!isCustomer && (
        <button
          type="button"
          onClick={handleDelete}
          aria-label={`Удалить пару ${group.combinedFormats.join('+')} ${title}`}
          title={`Удалить пару ${group.combinedFormats.join('+')}`}
          className="text-muted hover:text-rose"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            lineHeight: 0,
          }}
        >
          <Trash2 size={16} />
        </button>
        )}
      </div>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
